import { useState, type FormEvent } from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { alpha, useTheme } from '@mui/material/styles';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { useNavigate, useLocation, Navigate, Link as RouterLink } from 'react-router-dom';
import { login, verifyMfa, enrolMfa, enrolPin, getEntraConfig, startMicrosoftSignIn, loginWithMicrosoft, getSamlConfig, startSamlSignIn, loginWithSaml, type MfaChallenge, type MfaSetup, type EntraConfig, type SamlConfig } from '@/api/auth';
import { useEffect } from 'react';
import { useAuthStore } from '@/auth/authStore';
import { useUiStore } from '@/store/uiStore';
import { brandingAsset, useBranding } from '@/theme/useBranding';
import { brand } from '@/theme/palette';
import ProductMark from '@/components/common/ProductMark';

const appName = import.meta.env.VITE_APP_NAME ?? 'Annova ERP Plus';

function Logo({ size = 40, src }: { size?: number; src?: string | null }) {
  if (src) return <Box component="img" src={src} alt="" sx={{ height: size, maxWidth: size * 3, objectFit: 'contain', flexShrink: 0 }} />;
  return <ProductMark size={size} />;
}

export default function LoginPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const colorMode = useUiStore((s) => s.colorMode);
  const toggleColorMode = useUiStore((s) => s.toggleColorMode);
  const isLight = useTheme().palette.mode === 'light';
  const branding = useBranding();
  const background = brandingAsset(branding?.loginBackgroundUrl);
  const orgName = branding?.organisationName ?? appName;
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // The second factor: the challenge from a correct password, the code typed, and the set-up key when enrolling.
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
  const [code, setCode] = useState('');
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  // Enrolling with a sign-in PIN instead of an authenticator, where the organisation allows it.
  const [pinEnrol, setPinEnrol] = useState<{ pin: string; again: string } | null>(null);
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [entra, setEntra] = useState<EntraConfig | null>(null);

  const [saml, setSaml] = useState<SamlConfig | null>(null);

  useEffect(() => { getEntraConfig().then(setEntra).catch(() => setEntra(null)); }, []);
  useEffect(() => { getSamlConfig().then(setSaml).catch(() => setSaml(null)); }, []);
  // Back from the SAML provider: a one-time code (or the reason it was refused) in the query string.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const samlCode = params.get('saml');
    const samlError = params.get('samlError');
    if (!samlCode && !samlError) return;
    const returnTo = sessionStorage.getItem('saml.from') ?? '/';
    window.history.replaceState(null, '', window.location.pathname);
    if (samlError || !samlCode) { setError(samlError ?? 'The sign-in was refused.'); return; }
    setSubmitting(true);
    loginWithSaml(samlCode).then(() => navigate(returnTo, { replace: true })).catch((err: Error) => setError(err.message)).finally(() => setSubmitting(false));
  }, [navigate]);
  // Back from Microsoft: the ID token rides in the URL fragment.
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash.includes('id_token=')) return;
    const params = new URLSearchParams(hash.slice(1));
    const idToken = params.get('id_token');
    const returnTo = sessionStorage.getItem('entra.from') ?? '/';
    window.history.replaceState(null, '', window.location.pathname);
    if (!idToken) { setError(params.get('error_description') ?? 'Microsoft did not return a sign-in.'); return; }
    setSubmitting(true);
    loginWithMicrosoft(idToken).then(() => navigate(returnTo, { replace: true })).catch((err: Error) => setError(err.message)).finally(() => setSubmitting(false));
  }, [navigate]);

  if (isAuthenticated && !recovery) return <Navigate to={from} replace />;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await login(email.trim(), password);
      if ('mfaRequired' in result) {
        if (result.enrolmentRequired) {
          // The authenticator key is made now; the person may still choose a PIN instead when allowed.
          const s = await enrolMfa(result.mfaToken);
          setSetup(s);
          setChallenge({ ...result, mfaToken: s.mfaToken ?? result.mfaToken });
        } else setChallenge(result);
        return;
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    setError(null);
    setSubmitting(true);
    try {
      if (pinEnrol) {
        await enrolPin(challenge.mfaToken, pinEnrol.pin);
        navigate(from, { replace: true });
        return;
      }
      const { recoveryCodes } = await verifyMfa(challenge.mfaToken, code);
      if (recoveryCodes && recoveryCodes.length) { setRecovery(recoveryCodes); return; }
      navigate(from, { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const methods = challenge?.methods ?? [];
  const codePrompt = methods.includes('Authenticator') && methods.includes('Pin')
    ? 'Enter the six-digit code from your authenticator app, your sign-in PIN, or a recovery code.'
    : methods.includes('Pin')
      ? 'Enter your sign-in PIN.'
      : 'Enter the six-digit code from your authenticator app, or a recovery code.';
  const pinEnrolReady = pinEnrol !== null && /^\d{4,8}$/.test(pinEnrol.pin) && pinEnrol.pin === pinEnrol.again;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        p: 2,
        // The organisation's own background picture when it has one (docs §273, URS ADM-01), dimmed so the form stays readable.
        background: background
          ? `linear-gradient(${isLight ? 'rgba(243,242,241,0.55)' : 'rgba(27,26,25,0.65)'}, ${isLight ? 'rgba(243,242,241,0.55)' : 'rgba(27,26,25,0.65)'}), center / cover no-repeat url("${background}")`
          : isLight
            ? `radial-gradient(1200px 600px at 50% -10%, ${alpha(brand.primary, 0.14)} 0%, transparent 60%), #F3F2F1`
            : `radial-gradient(1200px 600px at 50% -10%, ${alpha(brand.primary, 0.28)} 0%, transparent 60%), #1B1A19`,
      }}
    >
      {/* Colour-mode toggle */}
      <Tooltip title={colorMode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
        <IconButton
          onClick={toggleColorMode}
          aria-label="Toggle colour mode"
          sx={{
            position: 'absolute', top: 16, right: 16, zIndex: 10,
            color: 'text.secondary', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
            '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
          }}
        >
          {colorMode === 'light' ? <DarkModeOutlinedIcon fontSize="small" /> : <LightModeOutlinedIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      <Paper
        elevation={0}
        sx={{
          width: '100%', maxWidth: 400, p: { xs: 3, sm: 4.5 }, borderRadius: 2,
          border: '1px solid', borderColor: 'divider',
          boxShadow: isLight
            ? '0 3.2px 7.2px rgba(0,0,0,0.13), 0 0.6px 1.8px rgba(0,0,0,0.11)'
            : '0 3.2px 7.2px rgba(0,0,0,0.5)',
        }}
      >
        <Stack direction="row" alignItems="center" gap={1.25} sx={{ mb: 3 }}>
          <Logo src={branding?.logoDataUrl} />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.1 }}>{orgName}</Typography>
            <Typography variant="caption" color="text.secondary">{branding && branding.organisationName !== appName ? appName : 'Board portal and strategy'}</Typography>
          </Box>
        </Stack>

        <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>Sign in</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {branding?.loginMessage ?? `Use your ${appName} account to continue.`}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {recovery && (
          <Stack spacing={2}>
            <Alert severity="success"><b>Your authenticator is set up.</b> Keep these recovery codes somewhere safe; each signs you in once if you lose the app.</Alert>
            <Box component="pre" sx={{ m: 0, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontFamily: 'monospace', fontSize: 14 }}>{recovery.join('\n')}</Box>
            <Button variant="contained" size="large" onClick={() => navigate(from, { replace: true })}>Continue</Button>
          </Stack>
        )}
        {challenge && !recovery && (
          <Box component="form" onSubmit={handleCode}>
            <Stack spacing={2}>
              {setup && pinEnrol ? (
                <>
                  <Typography variant="body2">Your organisation requires a second factor. Choose a sign-in PIN of four to eight digits; you will enter it after your password each time.</Typography>
                  <TextField label="PIN" type="password" value={pinEnrol.pin} onChange={(e) => setPinEnrol({ ...pinEnrol, pin: e.target.value.replace(/\D/g, '') })} inputProps={{ inputMode: 'numeric', maxLength: 8 }} required fullWidth autoFocus />
                  <TextField label="The PIN again" type="password" value={pinEnrol.again} onChange={(e) => setPinEnrol({ ...pinEnrol, again: e.target.value.replace(/\D/g, '') })} inputProps={{ inputMode: 'numeric', maxLength: 8 }} required fullWidth
                    error={pinEnrol.again.length > 0 && pinEnrol.again !== pinEnrol.pin} helperText={pinEnrol.again.length > 0 && pinEnrol.again !== pinEnrol.pin ? 'The PINs differ.' : ' '} />
                  <Button type="submit" variant="contained" size="large" disabled={submitting || !pinEnrolReady}>{submitting ? 'Saving\u2026' : 'Set the PIN and continue'}</Button>
                  <Button size="small" onClick={() => setPinEnrol(null)}>Use an authenticator app instead</Button>
                </>
              ) : setup ? (
                <>
                  <Typography variant="body2">Your organisation requires a second factor. Add an account in Microsoft Authenticator, Google Authenticator or Authy with this key, then enter the code it shows:</Typography>
                  <Box sx={{ fontFamily: 'monospace', fontSize: 15, letterSpacing: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, wordBreak: 'break-all' }}>{setup.sharedKey}</Box>
                </>
              ) : (
                <Typography variant="body2">{codePrompt}</Typography>
              )}
              {!pinEnrol && (
                <>
                  <TextField label={methods.includes('Pin') && !methods.includes('Authenticator') ? 'PIN' : 'Code'} type={methods.includes('Pin') && !methods.includes('Authenticator') ? 'password' : 'text'}
                    value={code} onChange={(e) => setCode(e.target.value)} inputProps={{ inputMode: 'numeric' }} required fullWidth autoFocus />
                  <Button type="submit" variant="contained" size="large" disabled={submitting || code.trim().length < (methods.includes('Pin') ? 4 : 6)}>{submitting ? 'Checking\u2026' : 'Continue'}</Button>
                  {setup && challenge.pinAllowed && (
                    <Button size="small" onClick={() => { setPinEnrol({ pin: '', again: '' }); setError(null); }}>No authenticator app? Set a sign-in PIN instead</Button>
                  )}
                </>
              )}
              <Button size="small" onClick={() => { setChallenge(null); setSetup(null); setPinEnrol(null); setCode(''); }}>Start again</Button>
            </Stack>
          </Box>
        )}
        {!challenge && !recovery && (
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required fullWidth autoFocus />
            <TextField label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required fullWidth />
            {entra?.enabled && (
              <Button variant="outlined" size="large" disabled={submitting} onClick={() => startMicrosoftSignIn(entra, from)}>Sign in with Microsoft</Button>
            )}
            {saml?.enabled && (
              <Button variant="outlined" size="large" disabled={submitting} onClick={() => startSamlSignIn(from)}>{saml.displayName}</Button>
            )}
            <Button type="submit" variant="contained" size="large" disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
            <Link component={RouterLink} to="/forgot-password" sx={{ textAlign: 'center', fontSize: 14 }}>Forgot password?</Link>
            <Link component={RouterLink} to="/signup" sx={{ textAlign: 'center', fontSize: 14 }}>New organisation? Create one</Link>
          </Stack>
        </Box>
        )}
      </Paper>

      <Typography variant="caption" sx={{ position: 'absolute', bottom: 14, color: 'text.secondary' }}>
        © {new Date().getFullYear()} {appName}
      </Typography>
    </Box>
  );
}
